import React, { useMemo, useContext } from 'react';
import cn from 'classnames';
import siblingPlaceholder from './assets/sibling-placeholder.png';
import calendarPlaceholder from './assets/calendar-placeholder.png';
import cardPlaceholder from './assets/card-placeholder.png';
import ornamentPlaceholder from './assets/ornament-placeholder.png';
import magnetPlaceholder from './assets/magnet-placeholder.png';
import styles from './HolidayBuilder.module.scss';
import DisplayableSheet from '../../../PackageBuilders/MultiPose/DisplayableSheet';
import { addonSheetToSheet } from '../../../PackageBuilders/MultiPose/functions';
import Image from "../../../Image";
import PreviewImage from '../../../PreviewImage';
import defaultBackground from "../../../../assets/Package_Blue.png";
import blackImage from './assets/PromoImage_Slide_1_Black.jpg';

import plaidBackground from './assets/Senior_Grey.jpg';
import SchoolAnnouncementThumbnail from './SchoolAnnouncementThumbnail';
import GradMagnet from './GradMagnet';
import { CURSIVE_BASE_URL, CURSIVE_OVERLAY_URL as cursiveForeground } from './announcementThumbnail';
import { CustomerContext } from '../../../../contexts/CustomerContext';
import reportUnknownAddonVariant from '../../../../utils/addonPreviewVariant';
import axios from 'axios';
import '../../../../fonts/fonts.scss';

// Variants that draw the portrait itself rather than compositing it into a
// product mockup, and so need no `addon.imageUrl`. Keep this in step with the
// cases below that ignore `addon.imageUrl`.
// `gradmagnet` bundles its own fridge and overlay (GradMagnet.jsx), so it too
// draws without an upload.
const SELF_DRAWING_VARIANTS = ['digitalfile', 'gradmagnet', 'gradyearmagnet'];

/**
 * Can this add-on's preview actually be DRAWN?
 *
 * Every variant here composites the pose into a photograph of a physical
 * product, so an add-on with no product photo draws an empty box whatever its
 * css_name. SELF_DRAWING_VARIANTS are the exception: they ARE the portrait and
 * have no product to photograph.
 *
 * Exported because the caller has to know BEFORE rendering: GiftTile shows a
 * labelled "Product photo" placeholder when there is nothing to draw, and it
 * cannot tell an empty preview from a present one after the fact.
 */
export const canRenderPreview = (addon, variant) => (
  variant !== 'sibling' && (!!addon?.imageUrl || SELF_DRAWING_VARIANTS.includes(variant))
);

// THE CUSTOMER'S POSE, WITH THE BACKGROUND THEY CHOSE BEHIND IT.
//
// ⚠️ This page drew the pose as a bare <Image> and composited NO background at
// all -- for any product. A parent picking a background on the builder saw the
// preview never change (reported 2026-10-14). Worse, a pose is usually a PNG
// cut-out with a transparent surround, so whatever sat behind showed through:
// the product artwork's own stand-in scene. Parents were looking at a classroom
// or a chalkboard baked into the mockup and reading it as their background.
//
// `PreviewImage` is the compositor the cart and package builder already use
// (DisplayableSheet), so this adds no second way to put a background behind a
// pose -- and the two surfaces now agree about what a product looks like.
//
// It draws a background only behind a `.png` pose, which is correct rather than
// a limitation: a JPEG pose has its backdrop baked in and nothing to show
// through.
function PoseOnProduct({ pose, addon, background }) {
  return (
    <PreviewImage
      // Never the access-code placeholder: this page has always shown the
      // watermarked pose itself, and that is what the rest of the tile is
      // positioned against.
      isPreviewHidden={false}
      foregroundUrl={pose?.watermarkUrl}
      backgroundUrl={backgroundForProduct(addon, background)}
    />
  );
}

// ⚠️ `backgroundSelection` IS THE PRODUCT'S OWN SWITCH, and the same rule
// DisplayableSheet applies in `backgroundForSheet`. A product whose mockup has
// no visible backdrop must not get a rectangle of studio canvas behind its pose.
function backgroundForProduct(addon, background) {
  if (!addon?.backgroundSelection) return undefined;

  return background?.imageUrl || undefined;
}

export default function Item({
  siblingProps, addon, variant, titleText, subtitleText, onClick = () => {}, pose, background,
  // Render ONLY the composited preview -- no tile chrome, no button, no type.
  // GiftTile owns the square box, the name, the price and the action now; this
  // component keeps what only it knows, which is how each variant is drawn.
  previewOnly = false,
}) {
  const { customer } = useContext(CustomerContext);

  // Removed here: a ResizeObserver + window resize listener that measured
  // `document.getElementById('item-wrapper')` into a `componentWidth` state
  // nothing ever read. Every tile on the grid carried that same id, so all of
  // them observed the FIRST tile in the document, and each one re-rendered the
  // whole card on every resize frame to store a number it did not use.

  const renderImageComponent = () => {
    switch (variant) {
      case 'ornament':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.ornament}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
      case 'gradannouncementregular':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.gradannouncementregular}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
        case 'gradbrown':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.gradbrown}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
      case 'gradplaid':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.gradplaid}>
              
              <Image src={plaidBackground} alt=""/>
              <Image src={pose?.watermarkUrl} alt="" classes={styles.gradplaidForeground}/>
            </div>
          </div>
        )
        case 'gradcursive':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={CURSIVE_BASE_URL} alt="" classes={styles.packageImage} />
            <Image src={cursiveForeground} alt="" classes={styles.gradcursiveForeground}/>
            <div className={styles.gradcursive}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
        case 'gradclassicvert':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.gradclassicvert}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
      case 'gradannouncement':
        return (
          <div className={styles.itemImgWrapper}>
            <SchoolAnnouncementThumbnail
              poseUrl={pose?.watermarkUrl}
              fallbackSrc={blackImage}
              classes={styles.packageImage}
            />
            <div className={styles.gradannouncement}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
      case 'card':
        return cardPlaceholder;
      case 'keychain':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.poseOneKeychain}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        )
      case 'magnet':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.poseOneMagnet}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
            <div className={styles.poseTwoMagnet}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        );
      // THE GRADUATE MAGNET: pose + background through an overlay's window,
      // with "CLASS OF YYYY" and the student's name in the type the lab prints.
      // Drawn by GradMagnet for this page AND the cart, so the geometry has
      // one home; this case only hands over what the page knows. `poses` is
      // not a prop here, so the class-year fallback sees the magnet's own pose.
      // The fridge and the overlay are bundled, so `addon.imageUrl` is unused.
      case 'gradmagnet':
      case 'gradyearmagnet':
        return (
          <GradMagnet
            variant={variant}
            pose={pose}
            backgroundUrl={backgroundForProduct(addon, background)}
          />
        );
        case 'calendar':
          return (
            <div className={styles.itemImgWrapper}>
              <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
              
              <div className={styles.calendar}>
                <PoseOnProduct pose={pose} addon={addon} background={background} />
              </div>
            </div>
          );

          // The week-at-a-glance dry-erase calendar. Its photo slot sits lower
          // and further right than the month calendar's, so it needs its own
          // geometry rather than borrowing `calendar`.
          case 'calendarweek':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />

                <div className={styles.calendarweek}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );

          // The weekly dry-erase board whose photo is a CUTOUT standing on the
          // board, not a photo in a printed frame. Drawn exactly like the
          // others -- a pose is already a transparent PNG, so the artwork
          // simply shows through around the student. All that differs is where
          // `.weeklycutout` puts it; the comment there owns the numbers.
          case 'weeklycutout':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />

                <div className={styles.weeklycutout}>
                  <Image src={pose?.watermarkUrl} alt=""/>
                </div>
              </div>
            );

          case 'eightbytenG':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
                
                <div className={styles.eightbytenG}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );

        // A DIGITAL FILE IS NOT A PRINT, and must not borrow eightbytenG's
        // frame to say so: the two sit side by side on this page at different
        // prices, and the same 8x10 mockup on both sells a download as a
        // photograph.
        //
        // Every other variant composites the pose INTO a photograph of a
        // physical product. There is no product to photograph here, and the
        // add-on carries no image to fall back on -- so this draws the
        // portrait itself, which is literally what the customer receives.
        case 'digitalfile':
          return (
            <div className={styles.itemImgWrapper}>
              <div className={styles.digitalfile}>
                <PoseOnProduct pose={pose} addon={addon} background={background} />
              </div>
            </div>
          );
            case 'coffeecup':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
                
                <div className={styles.coffeecup}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );
          case 'fivebyseven':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
                
                <div className={styles.poseOnefivebyseven}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseTwofivebyseven}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );
          case 'threebyfive':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
                
                <div className={styles.poseOnethreebyfive}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseTwothreebyfive}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseThreethreebyfive}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseFourthreebyfive}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );
          case 'wallets':
            return (
              <div className={styles.itemImgWrapper}>
                <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
                
                <div className={styles.poseOneWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseTwoWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseThreeWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseFourWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>

                <div className={styles.poseFiveWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseSixWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseSevenWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
                <div className={styles.poseEightWallet}>
                  <PoseOnProduct pose={pose} addon={addon} background={background} />
                </div>
              </div>
            );
      case 'plaque':
        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
            <div className={styles.poseOnePlaque}>
              <PoseOnProduct pose={pose} addon={addon} background={background} />
            </div>
          </div>
        );
      default:
        // A css_name no preview knows (a print-template code like `T810`, a typo).
        // Draw the add-on's own product image so the card is visible and clickable
        // instead of an empty box — the pose overlay needs per-variant geometry
        // that doesn't exist for an unknown value. See utils/addonPreviewVariant.
        reportUnknownAddonVariant(addon, variant);

        return (
          <div className={styles.itemImgWrapper}>
            <Image src={addon.imageUrl} alt="" classes={styles.packageImage} />
          </div>
        );
    }
  }

  const sheets = useMemo(() => {
    if (variant !== 'sibling') return [];

    return siblingProps.addons.map((addon) => addonSheetToSheet({ addon, background: siblingProps.background, pose: siblingProps.pose }));
  }, [variant, siblingProps]);

  // The preview alone, for GiftTile. Returns null rather than an empty element
  // when there is nothing to draw, so the caller's placeholder can take over.
  if (previewOnly) {
    if (!canRenderPreview(addon, variant)) return null;

    // ⚠️ THE TWO WRAPPER CLASSES ARE LOAD-BEARING, not layout tidiness. Every
    // variant's pose overlay is positioned as a percentage of `.itemImgWrapper`,
    // and that geometry is nested under `.item .previewSlot` in the stylesheet.
    // Returning the bare component dropped both ancestors, so no overlay rule
    // matched: the tiles rendered the product mockups with the BACKGROUND only
    // and no student in them — a shelf of empty frames, on a page whose whole
    // pitch is "made from your own portraits".
    return (
      <div className={cn(styles.item, 'h-full w-full')}>
        <div className={cn(styles.previewSlot, 'h-full w-full')}>
          {renderImageComponent()}
        </div>
      </div>
    );
  }

  return (
    // border-0 and an explicit background are NOT decoration: Tailwind's
    // preflight is off (tailwind.config.js) so Bootstrap's Reboot still leaves a
    // bare <button> with the user-agent border and grey `buttonface` fill, which
    // drew a box around every tile.
    // NO CARD. The page is white, so a white card with a shadow would be an
    // outline around nothing — the artwork's own square is what separates one
    // product from the next.
    //
    // border-0 and bg-transparent are NOT decoration: Tailwind's preflight is
    // off (tailwind.config.js) so Bootstrap's Reboot still leaves a bare
    // <button> with the user-agent border and grey `buttonface` fill, which drew
    // a box around every tile.
    <button
      className={cn(
        styles.item,
        'group flex h-full w-full flex-col border-0 bg-transparent p-0 text-left',
        // The whole card lifts on hover, which is what tells a shopper the card
        // itself is the target — the tile carries no button of its own, so the
        // card IS the target at every width.
        'transition-transform duration-150 hover:-translate-y-[3px]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-gift-accent',
      )}
      type="button"
      onClick={onClick}
    >
      {/* ONE SQUARE SLOT PER TILE. Letting each image size itself gave every
          grid row a different height and left the titles on a ragged baseline.
          Square costs nothing: every gifts-page mockup is 1:1, so each fills the
          slot with no crop. `previewSlot` in the stylesheet does the sizing.

          The pose overlays are unaffected: their geometry is percentages of
          .itemImgWrapper, which still hugs the product image exactly. */}
      {/* The photo tile: a neutral ground with a hairline ring, so a mockup
          whose own backdrop is near-white still reads as a product ON something
          rather than floating. */}
      <div
        className={cn(
          styles.previewSlot,
          'w-full overflow-hidden rounded-gift-img bg-gift-tile',
          'ring-1 ring-inset ring-black/5',
        )}
      >
        {/* ⚠️ `addon.imageUrl &&` GATES THE WHOLE PREVIEW. Every variant here
            composites the pose into a photograph of a physical product, so an
            add-on with no product photo drew an empty box whatever its
            css_name -- which is why "Digital File" could not have been shown on
            this page even with a correct variant.

            SELF_DRAWING_VARIANTS are the exception: they ARE the portrait and
            have no product to photograph, so they must render without one. */}
        {variant !== 'sibling'
          && (addon.imageUrl || SELF_DRAWING_VARIANTS.includes(variant))
          && renderImageComponent()}
        {/*{variant === 'sibling' && sheets.map((sheet) => <DisplayableSheet sheet={sheet} />)}*/}
      </div>

      {/* NAME AND PRICE, AND NOTHING ELSE.
          `mt-auto` pushes the price to the tile's bottom edge so prices land on
          one line across a row even when a name wraps to two.

          No description: the shelf card names the product and prices it, and the
          product's own screen is where it gets described. A one-line spec under
          every name also meant a row of tiles where some had two lines of grey
          copy and some had none, which read as missing data rather than as a
          deliberately short card.

          No "Personalize" button either. The whole tile is the <button> — a
          control inside a control is one more thing to miss, and it repeated on
          every card an instruction the card itself already gives. This is what
          the phone layout already did; it now holds at every width. */}
      <div className="flex flex-1 flex-col px-1 pt-3">
        {/* ⚠️ NO `font-bold` HERE, AND NOT BECAUSE IT LOOKED WRONG -- IT DOES
            NOTHING. Avenir is registered one @font-face per weight, each under
            its own family name and with no font-weight descriptor, so
            AvenirNextLTPro-Regular is only ever known at 400 and asking it for
            700/800 changes no pixels (measured: 208.4px at every weight).
            To make text on this page heavier, switch font-family to
            'AvenirNextLTPro-Demi' or '-Bold'; a font-weight class is decoration
            that reads as intent and delivers none. */}
        <span className="text-[13px] leading-tight text-gift-ink">{titleText}</span>

        <span className="mt-auto pt-1 text-[13px] leading-snug text-gift-ink">
          {subtitleText}
        </span>
      </div>
    </button>
  );
}
